import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, Crown } from "lucide-react";
import { api } from "../lib/api";
import { loadRecordings } from "../lib/audio";
import { Recorder } from "../components/Recorder";

const KINDS = ["全部", "標題", "注音", "國字", "詞語", "句子", "旁白", "對話"];

export default function Recordings() {
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [sel, setSel] = useState(null);
  const [kind, setKind] = useState("全部");
  const load = () => api.get("/recordings/items").then(({ data }) => { setGroups(data); setSel((s) => s || data[0]?.story_id); });
  useEffect(() => { load(); loadRecordings(); }, []);
  const g = groups.find((x) => x.story_id === sel);
  const total = groups.reduce((a, x) => a + x.items.filter((i) => i.recording_id).length, 0);
  const items = (g?.items || []).filter((i) => kind === "全部" || i.kind === kind);

  return (
    <div className="min-h-screen paper-bg font-serif-tc" data-testid="recordings-page">
      <header className="sticky top-0 z-20 bg-amber-100/90 backdrop-blur-md border-b-2 border-amber-300/80">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <button data-testid="recordings-back-btn" onClick={() => nav("/dashboard")} className="w-10 h-10 rounded-full bg-white border flex items-center justify-center"><ArrowLeft size={18} /></button>
          <Mic className="text-[var(--vermilion)]" />
          <div className="font-black text-2xl text-[var(--tea)]">家長錄音室</div>
          <div className="ml-auto text-sm text-stone-600" data-testid="recordings-total">已錄 {total} 段</div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <p className="text-stone-700 leading-relaxed">按下紅色麥克風開始錄音，再按一次停止。錄好之後，您的孩子學習時就會聽到您的聲音，取代電腦發音。可以隨時重錄或刪除。</p>
        <div className="flex gap-2 overflow-x-auto py-5" data-testid="recordings-story-tabs">
          {groups.map((x) => (
            <button key={x.story_id} data-testid={`recordings-story-${x.story_id}`} onClick={() => setSel(x.story_id)}
              className={`shrink-0 px-4 h-11 rounded-full border-2 font-bold ${sel === x.story_id ? "bg-[var(--vermilion)] text-white border-transparent" : "bg-white border-[var(--line)]"}`}>
              {x.story_number ? `${x.story_number}. ` : ""}{x.story_title}{x.premium && <Crown size={14} className="inline ml-1 -mt-1 text-amber-500" data-testid={`recordings-story-premium-${x.story_id}`} />}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {KINDS.map((k) => <button key={k} data-testid={`recordings-kind-${k}`} onClick={() => setKind(k)} className={`px-3 h-9 rounded-full text-sm font-bold border ${kind === k ? "bg-[var(--tea)] text-white border-transparent" : "bg-white border-[var(--line)]"}`}>{k}</button>)}
        </div>
        <div className="space-y-3" data-testid="recordings-list">
          {g?.premium && (
            <div className="scroll-card !rounded-2xl p-6 text-center" data-testid="recordings-premium-lock">
              <div className="font-black text-xl text-[var(--tea)]">這一課是 Premium 內容</div>
              <p className="text-stone-600 mt-1">升級 Premium 或開始免費試用，就能替所有課程錄音。</p>
              <button data-testid="recordings-premium-btn" onClick={() => nav("/pricing")} className="mt-4 h-11 px-6 rounded-full bg-[var(--vermilion)] text-white font-bold">查看方案</button>
            </div>
          )}
          {!g?.premium && items.map((it, i) => (
            <div key={it.text} className="scroll-card !rounded-2xl px-5 py-4 flex items-center gap-4" data-testid={`recording-row-${i}`}>
              <span className="shrink-0 px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-xs font-bold text-[var(--tea)]">{it.kind}</span>
              <div className="flex-1 min-w-0">
                <div className={`font-black ${it.kind === "旁白" || it.kind === "句子" || it.kind === "對話" ? "text-lg" : "text-3xl"}`}>{it.kind === "旁白" || it.kind === "對話" ? it.text : it.label}</div>
                {(it.kind === "旁白" || it.kind === "對話") && <div className="text-xs text-stone-400">{it.label}</div>}
                {it.kind === "注音" && <div className="text-xs text-stone-400">請唸出這個注音的音（參考：{it.text}）</div>}
              </div>
              {it.recording_id && <span className="text-xs text-green-700 font-bold shrink-0">已錄音</span>}
              <Recorder item={it} onChange={load} testid={`recorder-${i}`} />
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
